/**
 * StatusBadge (TASK-661) — the one status pill for the whole product.
 *
 * Anatomy: tinted background + border from a semantic status token, an icon,
 * and a text label. The label is required and the icon is on by default, so a
 * status is never communicated by color alone (WCAG 1.4.1). Announced to
 * assistive technology via role="status" + aria-label.
 *
 * Semantics (one meaning per hue, product-wide — docs/design/semantic-ui.md):
 *   info        neutral information / in-progress activity
 *   success     verified success / completed / compliant
 *   attention   needs attention / due soon / review required
 *   urgent      urgent, overdue, destructive, or blocking
 *   inferred    AI-inferred and not yet human-confirmed
 *   external    externally sourced (PMS / custodian / system of record)
 *   unavailable missing, not applicable, or not configured
 *   neutral     no status meaning (plain descriptive tag)
 */
import type { ReactNode } from 'react';
import {
  AlertOctagon,
  AlertTriangle,
  CheckCircle2,
  CircleSlash,
  Info,
  Landmark,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '../../lib/utils';

export type SemanticStatus =
  | 'info'
  | 'success'
  | 'attention'
  | 'urgent'
  | 'inferred'
  | 'external'
  | 'unavailable'
  | 'neutral';

type IconComponent = LucideIcon;

// Literal class strings — Tailwind's JIT cannot see dynamically-built names.
const STATUS_CLASSES: Record<SemanticStatus, string> = {
  info: 'border-status-info/30 bg-status-info/10 text-status-info-fg',
  success: 'border-status-success/30 bg-status-success/10 text-status-success-fg',
  attention: 'border-status-attention/30 bg-status-attention/10 text-status-attention-fg',
  urgent: 'border-status-urgent/30 bg-status-urgent/10 text-status-urgent-fg',
  inferred: 'border-status-inferred/30 bg-status-inferred/10 text-status-inferred-fg',
  external: 'border-status-external/30 bg-status-external/10 text-status-external-fg',
  unavailable:
    'border-status-unavailable/30 bg-status-unavailable/10 text-status-unavailable-fg',
  neutral: 'border-border bg-muted text-muted-foreground',
};

const DEFAULT_ICON: Record<SemanticStatus, IconComponent | null> = {
  info: Info,
  success: CheckCircle2,
  attention: AlertTriangle,
  urgent: AlertOctagon,
  inferred: Sparkles,
  external: Landmark,
  unavailable: CircleSlash,
  neutral: null,
};

const SIZE_CLASSES = {
  sm: 'px-2 py-0.5 text-[11px] gap-1',
  md: 'px-2.5 py-0.5 text-xs gap-1.5',
} as const;

const ICON_SIZE = { sm: 'h-3 w-3', md: 'h-3.5 w-3.5' } as const;

export interface StatusBadgeProps {
  status: SemanticStatus;
  /** The visible label — required so no status relies on color alone. */
  children: ReactNode;
  /** Override the default status icon; pass null to render text-only. */
  icon?: IconComponent | null;
  size?: keyof typeof SIZE_CLASSES;
  /** Accessible name; defaults to the visible label via role="status" content. */
  'aria-label'?: string;
  title?: string;
  className?: string;
}

export function StatusBadge({
  status,
  children,
  icon,
  size = 'md',
  title,
  className,
  ...aria
}: StatusBadgeProps) {
  const Icon = icon === undefined ? DEFAULT_ICON[status] : icon;
  return (
    <span
      role="status"
      title={title}
      aria-label={aria['aria-label']}
      data-status={status}
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full border font-medium',
        SIZE_CLASSES[size],
        STATUS_CLASSES[status],
        className,
      )}
    >
      {Icon && <Icon className={cn('shrink-0', ICON_SIZE[size])} aria-hidden />}
      {children}
    </span>
  );
}
